import React from 'react';

/**
 * OrganizationSchema Component
 * Critical for AI search and brand recognition
 * Helps Google/Bing/AI understand who you are as a business
 * 
 * IMPORTANT: Renders inline so Google can see it on first crawl
 */
const OrganizationSchema = () => {
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://audioandco.com/#organization",
    "name": "Audio & Co.",
    "legalName": "Audio & Co.",
    "alternateName": ["Audio and Co", "AudioandCo", "Audio & Company", "Audio & Co®"],
    "url": "https://audioandco.com",
    "logo": {
      "@type": "ImageObject",
      "url": "https://audioandco.com/logo.png",
      "width": 200,
      "height": 200
    },
    "image": "https://images.unsplash.com/photo-1590602847861-f357a9332bbc?w=1200&h=630&fit=crop",
    "description": "Done-for-you podcast production, YouTube management, book publishing and Authority Hub for coaches, consultants and small businesses. Pro Plan from £280/week.",
    "foundingDate": "2019",
    "founder": {
      "@type": "Person",
      "name": "Jo Day",
      "url": "https://audioandco.com/about"
    },
    "slogan": "Your Voice. Amplified.",
    "knowsAbout": [
      "Podcast Production",
      "Podcast Management",
      "Podcast Editing",
      "Podcast Launch",
      "YouTube Channel Management",
      "Video Production",
      "Video Editing",
      "Book Publishing",
      "Self Publishing",
      "Audiobook Production",
      "Content Marketing",
      "Content Repurposing",
      "SEO",
      "AI Search Visibility"
    ],
    "areaServed": [
      {"@type": "Country", "name": "United Kingdom"},
      {"@type": "Country", "name": "United States"},
      {"@type": "Country", "name": "Canada"},
      {"@type": "Country", "name": "Australia"}
    ],
    "serviceType": [
      "Podcast Production Services",
      "Podcast Management Services",
      "YouTube Management Services",
      "Book Publishing Services",
      "Audiobook Production",
      "Content Repurposing"
    ],
    "email": "hello@audioandco.com",
    "sameAs": [
      // Verified live Sep 2026 (Jo confirmed handle @audio_andco, plus
      // audioandco variant on YouTube/X).
      "https://www.instagram.com/audio_andco",
      "https://www.facebook.com/audio_andco",
      "https://www.tiktok.com/@audio_andco",
      "https://x.com/audioandco",
      "https://www.youtube.com/@audioandco"
      // LinkedIn omitted — /company/audioandco and /company/audio_andco
      // both 404; add back when we have the correct URL.
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "email": "hello@audioandco.com",
      "availableLanguage": ["English"]
    },
    // aggregateRating removed Sep 2026. Google penalises AggregateRating
    // schema without a verifiable review source (Trustpilot, Google
    // Business Profile, or `Review` objects with author + reviewBody).
    // Add back once we connect a real review platform.
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Audio & Co Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "The Pro Plan",
            "description": "Done-for-you weekly podcast + YouTube + Shorts + blog + Authority Hub. £280/week."
          },
          "price": "280",
          "priceCurrency": "GBP",
          "url": "https://audioandco.com/pro-plan"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Podcast Management Services",
            "description": "Bespoke standalone podcast management: editing, publishing, show notes, guest management, distribution."
          },
          "url": "https://audioandco.com/podcast-management"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Websites & Apps",
            "description": "Custom website and mobile app builds, project-based, quoted on a discovery call."
          },
          "url": "https://audioandco.com/websites-apps"
        }
      ]
    }
  };

  return (
    <script
      type="application/ld+json"
      data-schema="organization"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }}
    />
  );
};

export default OrganizationSchema;
